'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Loader2, Skull, Sparkles, Volume2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { MafiaSettings } from './components/mafia-settings'
import { AtirameLogoMenu } from './components/atirame-logo-menu'
import { useMafiaAudio } from './audio/use-mafia-audio'
import {
  loadLlmProviderSettings,
  toLlmProviderPayload,
  type MafiaLlmProviderSettings,
} from './lib/llm-provider-settings'

interface SessionSummary {
  id: string
  status: string
  winner?: string
  createdAt: string
  tokensSpent: number
}

function sessionStatusLabel(session: SessionSummary) {
  if (session.status === 'cancelled') return 'متوقف شده'
  if (session.status === 'finished') {
    return `پایان — ${session.winner === 'mafia' ? 'مافیا' : 'شهروند'}`
  }
  if (session.status === 'paused_tokens') return 'متوقف — توکن'
  return 'در جریان'
}

export default function MafiaLandingPage() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [llmSettings, setLlmSettings] = useState<MafiaLlmProviderSettings | null>(
    null
  )
  const audio = useMafiaAudio()

  useEffect(() => {
    setLlmSettings(loadLlmProviderSettings())
  }, [])

  const loadSessions = useCallback(async () => {
    const res = await fetch('/api/mafia/sessions')
    if (res.status === 401) {
      setError('برای بازی باید وارد حساب آتیرام شوید.')
      return
    }
    if (!res.ok) return
    const data = await res.json()
    setSessions(data.sessions ?? [])
  }, [])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  const startGame = async () => {
    setLoading(true)
    setError(null)
    try {
      const settings = llmSettings ?? loadLlmProviderSettings()
      const res = await fetch('/api/mafia/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          llmProvider: toLlmProviderPayload(settings),
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error ?? 'خطا در ساخت بازی')
        return
      }
      void audio.play('click')
      router.push(`/mafia/${data.session.id}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div dir="rtl" className="min-h-screen">
      <div className="mx-auto flex max-w-3xl flex-col gap-10 px-4 py-12">
        <div className="flex items-center justify-between gap-3">
          <AtirameLogoMenu />
          <MafiaSettings
            audioReady={audio.ready}
            audioMuted={audio.prefs.muted}
            musicVolume={audio.prefs.music}
            sfxVolume={audio.prefs.sfx}
            onEnableAudio={() => void audio.enable()}
            onToggleMute={() =>
              audio.updatePrefs({ muted: !audio.prefs.muted })
            }
            onMusicVolume={(v) => audio.updatePrefs({ music: v })}
            onSfxVolume={(v) => audio.updatePrefs({ sfx: v })}
            onLlmSettingsChange={setLlmSettings}
          />
        </div>

        <header className="space-y-4 text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl"
            style={{
              background: 'rgba(155, 44, 44, 0.25)',
              border: '1px solid rgba(196, 60, 60, 0.35)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            }}
          >
            <Skull className="h-8 w-8 text-[var(--mafia-blood-bright)]" />
          </div>
          <h1 className="mafia-display text-4xl font-semibold tracking-tight text-[var(--mafia-ink)] sm:text-5xl">
            مافیا
          </h1>
          <p className="mx-auto max-w-md text-[var(--mafia-muted)] leading-relaxed">
            یک نفر واقعی + ۶ بازیکن AI روی میز شمع‌روشن — نقش‌ها تصادفی، مذاکره فارسی، و
            کسر اعتبار برای هر نوبت AI (یا کلید خودتان).
          </p>
        </header>

        {error && (
          <div
            className="rounded-[var(--mafia-radius)] border px-4 py-3 text-[var(--mafia-candle)]"
            style={{
              borderColor: 'rgba(212, 163, 92, 0.35)',
              background: 'rgba(42, 28, 18, 0.55)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          {!audio.ready && (
            <button
              type="button"
              onClick={() => void audio.enable()}
              className="flex items-center gap-2 text-xs text-[var(--mafia-muted)] hover:text-[var(--mafia-candle)]"
            >
              <Volume2 className="h-3.5 w-3.5" />
              برای فضای میز، صدا را روشن کنید
            </button>
          )}
          <Button
            size="lg"
            onClick={startGame}
            disabled={loading}
            className="min-w-52 bg-[var(--mafia-blood)] text-[var(--mafia-ink)] hover:bg-[var(--mafia-blood-bright)]"
          >
            {loading ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="ml-2 h-4 w-4" />
            )}
            شروع بازی جدید
          </Button>
          <p className="text-xs text-[var(--mafia-muted)]">
            ۷ بازیکن — ۲ مافیا، دکتر، کارآگاه، ۳ شهروند
            {llmSettings?.mode === 'byok' ? ' · حالت کلید شخصی' : ' · اعتبار آتیرام'}
          </p>
        </div>

        {sessions.length > 0 && (
          <section className="space-y-3">
            <h2 className="mafia-display text-lg font-semibold text-[var(--mafia-candle-dim)]">
              بازی‌های اخیر
            </h2>
            <ul className="space-y-2">
              {sessions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => router.push(`/mafia/${s.id}`)}
                    className="mafia-panel flex w-full items-center justify-between px-4 py-3 text-right transition hover:border-[var(--mafia-border-strong)]"
                  >
                    <span className="text-sm text-[var(--mafia-muted)]">
                      {sessionStatusLabel(s)}
                    </span>
                    <span className="font-medium">
                      بازی {new Date(s.createdAt).toLocaleDateString('fa-IR')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}
